// src/pages/account/NotificationsPanel.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Bell, BellRing, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TimePicker from "@/components/TimePicker";

import { testPush } from "@/api/account";
import {
  useNotifications,
  useUpdateNotifications,
} from "@/queries/account";
import {
  ensurePushSubscribed,
  ensurePushUnsubscribed,
  getPushPermission,
  getPushEnvironment,
  syncExistingPushSubscription,
} from "@/lib/pushNotifications";
import { useAuth } from "@/contexts/AuthContext";
import { useNotificationAttention } from "@/hooks/useNotificationAttention";
import { getNativePlatform } from "@/lib/runtime";
import { NativeNotificationsSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";

function buildNotificationForm(data) {
  if (!data) return null;
  const matchReminder = data.push?.match_reminder ?? { enabled: false, minutesBefore: 60 };
  const lineupUpdates = data.push?.lineup_updates ?? { enabled: false };
  const generalNews = data.push?.general_news ?? { enabled: false };

  return {
    email: data.email ?? {
      match_created: true,
      protocol_saved: true,
      conflict_detected: true,
    },
    push: {
      match_reminder: {
        enabled: !!matchReminder.enabled,
        minutesBefore: Number(matchReminder.minutesBefore ?? 60),
      },
      lineup_updates: { enabled: !!lineupUpdates.enabled },
      general_news: { enabled: !!generalNews.enabled },
    },
    quietHours: data.quietHours ?? { start: "22:00", end: "07:00" },
  };
}

export default function NotificationsPanel() {
  const { user } = useAuth();
  const { data, isLoading } = useNotifications();
  const update = useUpdateNotifications();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(() => buildNotificationForm(data));
  const reminderOptions = [15, 30, 45, 60, 90, 120];
  const isSuperAdmin = user?.role === "super_admin";
  const {
    isLoading: attentionLoading,
    permission,
    setPermission,
    needsNotificationsAttention,
  } = useNotificationAttention();

  useEffect(() => {
    if (!data) return;
    setForm(buildNotificationForm(data));
  }, [data]);

  useEffect(() => {
    if (!form) return;
    if (permission !== "granted") return;

    const pushEnabled =
      !!form.push?.match_reminder?.enabled ||
      !!form.push?.lineup_updates?.enabled ||
      !!form.push?.general_news?.enabled;
    if (!pushEnabled) return;

    let cancelled = false;

    (async () => {
      try {
        const synced = await syncExistingPushSubscription();
        if (!synced && !cancelled) {
          await ensurePushSubscribed();
        }
      } catch (_) {
        // Tyst backfill. Användaren ska inte få en toast bara för att sync försökts.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    form?.push?.match_reminder?.enabled,
    form?.push?.lineup_updates?.enabled,
    form?.push?.general_news?.enabled,
    form,
    permission,
  ]);

  const envOk = useMemo(() => getPushEnvironment(), []);
  const pushDemandedBySettings =
    !!form?.push?.match_reminder?.enabled ||
    !!form?.push?.lineup_updates?.enabled ||
    !!form?.push?.general_news?.enabled;

  const initialQuietHours = useMemo(
    () => ({
      start: data?.quietHours?.start ?? "22:00",
      end: data?.quietHours?.end ?? "07:00",
    }),
    [data]
  );

  const initialReminderMinutes = useMemo(
    () => Number(data?.push?.match_reminder?.minutesBefore ?? 60),
    [data]
  );

  const persist = async (next) => {
    if (!next) return;
    const mins =
      Math.max(1, parseInt(next.push?.match_reminder?.minutesBefore ?? 60, 10)) || 60;

    await update.mutateAsync({
      email: next.email ?? {},
      push: {
        match_reminder: {
          enabled: !!next.push?.match_reminder?.enabled,
          minutesBefore: mins,
        },
        lineup_updates: {
          enabled: !!next.push?.lineup_updates?.enabled,
        },
        general_news: {
          enabled: !!next.push?.general_news?.enabled,
        },
      },
      quietHours: next.quietHours ?? { start: "22:00", end: "07:00" },
    });
  };

  const quietHoursDirty =
    !!form &&
    ((form.quietHours?.start ?? "22:00") !== initialQuietHours.start ||
      (form.quietHours?.end ?? "07:00") !== initialQuietHours.end);

  const reminderMinutesDirty =
    !!form &&
    Number(form.push?.match_reminder?.minutesBefore ?? 60) !== initialReminderMinutes;

  const hasEnabledPushCategory = (next) =>
    !!next?.push?.match_reminder?.enabled ||
    !!next?.push?.lineup_updates?.enabled ||
    !!next?.push?.general_news?.enabled;

  const onToggleMatchReminder = async (checked) => {
    setSaving(true);
    try {
      const next = {
        ...form,
        push: {
          ...form.push,
          match_reminder: {
            ...form.push.match_reminder,
            enabled: checked,
          },
        },
      };

      if (checked) {
        await toast.promise(ensurePushSubscribed(), {
          loading: "Aktiverar matchpåminnelse...",
          success: "Matchpåminnelse aktiverad",
          error: (e) => e?.message || "Kunde inte aktivera",
        });
      } else if (!hasEnabledPushCategory(next)) {
        await toast.promise(ensurePushUnsubscribed(), {
          loading: "Stänger av matchpåminnelse...",
          success: "Matchpåminnelse avstängd",
          error: (e) => e?.message || "Kunde inte stänga av",
        });
      }

      setForm(next);
      await persist(next);
    } finally {
      setSaving(false);
    }
  };

  const onToggleLineupUpdates = async (checked) => {
    setSaving(true);
    try {
      const next = {
        ...form,
        push: {
          ...form.push,
          lineup_updates: { enabled: checked },
        },
      };

      if (checked) {
        await toast.promise(ensurePushSubscribed(), {
          loading: "Aktiverar lineup-notiser...",
          success: "Lineup-notiser aktiverade",
          error: (e) => e?.message || "Kunde inte aktivera",
        });
      } else if (!hasEnabledPushCategory(next)) {
        await ensurePushUnsubscribed();
      }

      setForm(next);
      await persist(next);
    } finally {
      setSaving(false);
    }
  };

  const onToggleGeneralNews = async (checked) => {
    setSaving(true);
    try {
      const next = {
        ...form,
        push: {
          ...form.push,
          general_news: { enabled: checked },
        },
      };

      if (checked) {
        await toast.promise(ensurePushSubscribed(), {
          loading: "Aktiverar Lapstr-notiser...",
          success: "Lapstr-notiser aktiverade",
          error: (e) => e?.message || "Kunde inte aktivera",
        });
      } else if (!hasEnabledPushCategory(next)) {
        await ensurePushUnsubscribed();
      }

      setForm(next);
      await persist(next);
    } finally {
      setSaving(false);
    }
  };

  const onChangeMinutes = (val) => {
    const minutes = Math.max(1, parseInt(val ?? "60", 10) || 60);
    setForm((current) => ({
      ...current,
      push: {
        ...current.push,
        match_reminder: {
          ...current.push.match_reminder,
          minutesBefore: minutes,
        },
      },
    }));
  };

  const onSaveReminderMinutes = async () => {
    if (!form || !reminderMinutesDirty) return;
    await toast.promise(persist(form), {
      loading: "Sparar påminnelsetid...",
      success: "Påminnelsetid uppdaterad",
      error: (e) => e?.message || "Kunde inte spara påminnelsetiden",
    });
  };

  const onSaveQuietHours = async () => {
    if (!form || !quietHoursDirty) return;
    await toast.promise(persist(form), {
      loading: "Sparar tysta timmar...",
      success: "Tysta timmar uppdaterade",
      error: (e) => e?.message || "Kunde inte spara tysta timmar",
    });
  };

  const onRequestSystemPermission = async () => {
    try {
      await toast.promise(ensurePushSubscribed(), {
        loading: "Aktiverar notiser...",
        success: "Notiser aktiverade",
        error: (e) => e?.message || "Kunde inte aktivera notiser",
      });
      setPermission(await getPushPermission());
    } catch {
      setPermission(await getPushPermission());
    }
  };

  const onOpenNotificationSettings = () => {
    toast("Tillåt notiser för Lapstr i webbläsarens eller enhetens inställningar för att dina valda notiser ska fungera.");
  };

  if (isLoading || attentionLoading || !form) {
    if (getNativePlatform() === "android") return <NativeNotificationsSkeleton />;

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5 shrink-0 opacity-70" />
            <span>Notiser</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="grid gap-6">
          <section className="grid gap-3">
            <div className="h-5 w-56 animate-pulse rounded bg-muted/50" />
            <div className="h-10 animate-pulse rounded-md bg-muted/50" />
            <div className="h-10 animate-pulse rounded-md bg-muted/50" />
            <div className="h-9 w-32 animate-pulse rounded-md bg-muted/50" />
          </section>

          <section className="grid gap-3">
            <div className="h-5 w-52 animate-pulse rounded bg-muted/50" />
            <div className="h-10 animate-pulse rounded-md bg-muted/50" />
          </section>

          <section className="grid gap-3">
            <div className="h-5 w-20 animate-pulse rounded bg-muted/50" />
            <div className="h-10 animate-pulse rounded-md bg-muted/50" />
            <div className="h-10 animate-pulse rounded-md bg-muted/50" />
            <div className="h-10 animate-pulse rounded-md bg-muted/50" />
          </section>

          <section className="grid gap-3">
            <div className="h-5 w-36 animate-pulse rounded bg-muted/50" />
            <div className="flex flex-wrap items-center gap-3">
              <div className="h-10 w-44 animate-pulse rounded-md bg-muted/50" />
              <div className="h-10 w-44 animate-pulse rounded-md bg-muted/50" />
            </div>
          </section>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 shrink-0 opacity-70" />
          <span>Notiser</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6">
        {needsNotificationsAttention ? (
          <section className="grid gap-3 rounded-xl border border-primary/25 bg-primary/5 p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 rounded-full bg-primary/10 p-2 text-primary">
                <BellRing className="h-4 w-4" />
              </div>
              <div className="grid gap-1">
                <Label className="text-foreground">Slutför notistillåtelse</Label>
                <p className="text-sm text-muted-foreground">
                  Du har aktiverat notiser i appen, men behöver också tillåta notiser i den här
                  enheten eller webbläsaren för att de ska fungera.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {permission === "denied" ? (
                <Button type="button" onClick={onOpenNotificationSettings}>
                  Så aktiverar du notiser
                </Button>
              ) : (
                <Button type="button" onClick={onRequestSystemPermission}>
                  Tillåt notiser
                </Button>
              )}
            </div>
          </section>
        ) : null}

        <section className="grid gap-3 rounded-2xl border border-border/70 bg-muted/15 p-4">
          <div className="grid gap-1">
            <Label>Matchpåminnelse</Label>
            <p className="text-sm text-muted-foreground">
              Pushnotis inför matchstart. Påminnelsetiden sparas separat.
            </p>
          </div>

          <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background/70 px-3 py-2.5">
            <span>Aktivera matchpåminnelse</span>
            <Switch
              disabled={
                isLoading || saving || !envOk.https || !envOk.sw || !envOk.push || !envOk.notif
              }
              checked={!!form.push?.match_reminder?.enabled}
              onCheckedChange={onToggleMatchReminder}
            />
          </div>

          <div className="flex flex-col gap-2 rounded-xl border border-border/60 bg-background/70 px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between">
            <span>Minuter före matchstart</span>
            <Select
              value={String(form.push?.match_reminder?.minutesBefore ?? 60)}
              onValueChange={onChangeMinutes}
            >
              <SelectTrigger className="w-full bg-background sm:w-40">
                <SelectValue placeholder="Välj tid" />
              </SelectTrigger>
              <SelectContent className="bg-card border border-border shadow-lg">
                {reminderOptions.map((minutes) => (
                  <SelectItem key={minutes} value={String(minutes)}>
                    {minutes} min
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-2">
            {reminderMinutesDirty && (
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    push: {
                      ...current.push,
                      match_reminder: {
                        ...current.push.match_reminder,
                        minutesBefore: initialReminderMinutes,
                      },
                    },
                  }))
                }
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Ångra
              </Button>
            )}
            <Button
              onClick={onSaveReminderMinutes}
              disabled={update.isPending || saving || !reminderMinutesDirty}
            >
              Spara påminnelsetid
            </Button>
            {isSuperAdmin ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  toast.promise(testPush(), {
                    loading: "Skickar test...",
                    success: "Test push skickad",
                    error: (e) => e?.message || "Kunde inte skicka test",
                  })
                }
                disabled={!form.push?.match_reminder?.enabled || saving}
              >
                Skicka test
              </Button>
            ) : null}
          </div>
        </section>

        <section className="grid gap-3 rounded-2xl border border-border/70 bg-muted/15 p-4">
          <div className="grid gap-1">
            <Label>Laguppställningar</Label>
            <p className="text-sm text-muted-foreground">
              Pushnotis när officiell laguppställning publiceras eller ändras för en match.
            </p>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background/70 px-3 py-2.5">
            <span>Aktivera lineup-notiser</span>
            <Switch
              disabled={
                isLoading || saving || !envOk.https || !envOk.sw || !envOk.push || !envOk.notif
              }
              checked={!!form.push?.lineup_updates?.enabled}
              onCheckedChange={onToggleLineupUpdates}
            />
          </div>
        </section>

        <section className="grid gap-3 rounded-2xl border border-border/70 bg-muted/15 p-4">
          <div className="grid gap-1">
            <Label>Nyheter och information från Lapstr</Label>
            <p className="text-sm text-muted-foreground">
              Pushnotiser för viktig information, större uppdateringar och manuella meddelanden från Lapstr.
            </p>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border/60 bg-background/70 px-3 py-2.5">
            <span>Aktivera Lapstr-notiser</span>
            <Switch
              disabled={
                isLoading || saving || !envOk.https || !envOk.sw || !envOk.push || !envOk.notif
              }
              checked={!!form.push?.general_news?.enabled}
              onCheckedChange={onToggleGeneralNews}
            />
          </div>
        </section>

        <section className="grid gap-3 rounded-2xl border border-border/70 bg-muted/15 p-4">
          <div className="grid gap-1">
            <Label>Tysta timmar</Label>
            <p className="text-sm text-muted-foreground">Gäller pushnotiser.</p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="w-10 text-sm text-muted-foreground">Från</span>
              <TimePicker
                label="Från"
                step={15}
                value={form.quietHours?.start ?? "22:00"}
                onChange={(time) =>
                  setForm((current) => ({
                    ...current,
                    quietHours: { ...(current.quietHours ?? {}), start: time },
                  }))
                }
              />
            </div>

            <div className="flex items-center gap-2">
              <span className="w-10 text-sm text-muted-foreground">Till</span>
              <TimePicker
                label="Till"
                step={15}
                value={form.quietHours?.end ?? "07:00"}
                onChange={(time) =>
                  setForm((current) => ({
                    ...current,
                    quietHours: { ...(current.quietHours ?? {}), end: time },
                  }))
                }
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            {quietHoursDirty && (
              <Button
                type="button"
                variant="ghost"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    quietHours: {
                      start: initialQuietHours.start,
                      end: initialQuietHours.end,
                    },
                  }))
                }
              >
                <RotateCcw className="mr-2 h-4 w-4" />
                Ångra
              </Button>
            )}
            <Button
              onClick={onSaveQuietHours}
              disabled={update.isPending || saving || !quietHoursDirty}
            >
              Spara tysta timmar
            </Button>
          </div>
        </section>
      </CardContent>
    </Card>
  );
}
